import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { TRICK_COLLECT_MS, TRICK_HOLD_MS } from "@bing/shared";
import { collectSweep } from "./card-motion";
import { carriedTrickWinner, type TrickRules } from "./trick-winner";
import type { TrickPlayLike, TrickViewLike } from "./use-table-sfx";

/*
 * THE END OF A TRICK IS ALWAYS SEEN — حکم's and بی‌دل's felts share this one machine.
 *
 * Operator, 2026-10-08: «sometimes the last card of a trick and the collect animation aren't shown —
 * the next player's card lands at once. Seeing which card was played last matters: always show it
 * and the full collect animation before the next trick's first card appears.»
 *
 * THE ASSUMPTION THAT WAS WRONG: each felt drew the LATEST frame and trusted the server to space them
 * (`TRICK_HOLD_MS + TRICK_COLLECT_MS` under the engines' 1100 ms before a BOT leads). But a HUMAN's
 * lead is sent the instant it is played (0 ms after the trick), a bot's has 150 ms of slack that any
 * stalled delivery eats, and a trump pick / a pass clears the carried trick at a hand boundary — the
 * first frame with any of those ended the trick at once: the 4th card pulled mid-flight, the collect
 * cancelled, the trick-result cue dropped.
 *
 * So the finished trick is LATCHED on the render where it first appears and stays on the felt for
 * the whole hold + collect from then, whatever newer frames say; then the live trick shows (cards
 * that landed meanwhile fly in from their seats at that moment). The timings did not change.
 *
 * THE HOLD IS ON THE WINNER'S TURN, AND THE SERVER GIVES IT BACK (card-a5 item 7, 2026-10-11): the
 * winner leads next, so their hand is disabled for the first `TRICK_HOLD_MS + TRICK_COLLECT_MS` of
 * their turn. The game-server ends that turn window exactly that much later
 * (`apps/game-server/src/rooms/trick-end-hold.ts`) and its `turn` frame says so — `endsAt` moved by
 * the hold, `totalMs` still the turn — so the ring stays full through the hold and counts the whole
 * turn from the moment the hand is live. Both numbers live in `@bing/shared` for that reason: the
 * felt and the deadline read the same ones.
 *
 * THE HAND'S LAST TRICK IS ALWAYS SEEN TOO (card-a5 item 9, «in rare cases the last trick of a hand
 * isn't shown at all»). A finished trick whose frame never reached this felt is caught by the
 * BACKSTOP below. Its source used to be the play log, and that cannot cover a hand's end: the deal
 * empties the play log in the very frame that carries the hand's last trick, and the call / a pass
 * pick / the 2♣ lead clears the carried copy. A client that missed that ONE frame — a silent
 * reconnect across the hand's end (the lifecycle shows nothing for its first 1.5 s), re-sent the
 * table after the clear; measured on staging — had nothing left to hold, so the hand's last trick
 * was never drawn. The game-server now names the match's newest finished trick and its winner in
 * `lastTrick`, through every deal, call, pass and lead, and the backstop reads that. The hold's
 * length did not change, so neither did `TRICK_END_HOLD_MS`.
 */

/** How long the four finished cards HOLD on the felt — the winning card included — before the
 *  collect starts; and the collect sweep after it. Their sum stays UNDER the engines' 1100 ms pause
 *  before a BOT's next lead (`postMoveDelayMs`), so a bot's lead never waits on it. Owned by
 *  `@bing/shared` (`domain/trick-hold.ts`): the server's turn window is lengthened by the same sum. */
export { TRICK_COLLECT_MS, TRICK_HOLD_MS };

/** What the hold reads off a frame — حکم's and بی‌دل's views both fit. */
export interface TrickHoldView extends TrickViewLike {
  /** Who leads the trick in play: once a trick completes, its WINNER — until the next one completes. */
  readonly leader: number;
  readonly playLog?: readonly (readonly TrickPlayLike[])[];
  /**
   * The match's newest FINISHED trick and the seat that took it, as the game-server names it — still
   * there after a deal, a call, a pass or a lead (card-a5 item 9). Absent from an older game-server.
   */
  readonly lastTrick?: { readonly plays: readonly TrickPlayLike[]; readonly winner: number } | null;
}

/**
 * `useTrickHold(view)` — call it in the TABLE (its hand and sounds read it), hand its result to the
 * felt. `holding`: a finished trick is on the felt for its hold + collect — nothing may put a card on
 * the felt (your hand's plays are disabled) until it is false. `shown`: what the felt shows, in the
 * shape the shared sound hooks read (`shownTrick(shown)` are its cards). `layerRef`: attach it to the
 * layer the collect sweeps.
 */
export interface TrickHold {
  readonly holding: boolean;
  readonly shown: TrickViewLike;
  readonly layerRef: RefObject<HTMLDivElement>;
}

interface Latch {
  /** The newest finished trick this felt has accounted for (`seat:card,…`); "" when none. */
  readonly sig: string;
  readonly plays: readonly TrickPlayLike[];
  /** Its winner — read off its cards (`carriedTrickWinner`), FROZEN when latched: the frames after
   *  it move `turn` on. */
  readonly winner: number;
  /** On the felt right now (its hold + collect is running). */
  readonly held: boolean;
}

const NOTHING: Latch = { sig: "", plays: [], winner: 0, held: false };

const sigOf = (plays: readonly TrickPlayLike[]): string =>
  plays.map((p) => `${p.seat}:${p.card}`).join(",");

/** The newest FINISHED trick this frame knows of — and whether the frame itself still shows it. */
function newestFinished(
  view: TrickHoldView,
  rules: TrickRules,
): (Omit<Latch, "held"> & { onFelt: boolean }) | null {
  const completed = view.completedTrick ?? [];
  if (view.currentTrick.length === 0 && completed.length === view.seats) {
    // The engines keep the four here until the next lead. Its winner is read off the CARDS
    // (review 6b F3): `turn` is the winner mid-hand, but in بی‌دل's hand-ending carry it is the NEW
    // deal's (0 in the pass, the 2♣ holder on a hold hand) — `turn` stays only as the fallback.
    const winner = carriedTrickWinner(completed, rules) ?? view.turn;
    return { sig: sigOf(completed), plays: completed, winner, onFelt: true };
  }
  // BACKSTOP — the frame that completed it never rendered (a reconnect, a frame lost to a dead
  // socket): the newest finished trick the frame still NAMES. Only ever the NEWEST, however many
  // were missed. The game-server's `lastTrick` (card-a5 item 9) names it at a hand's end as well,
  // with its winner — where the play log, emptied by the deal, names nothing.
  const named = view.lastTrick;
  if (named != null) {
    if (named.plays.length !== view.seats) return null;
    return { sig: sigOf(named.plays), plays: named.plays, winner: named.winner, onFelt: false };
  }
  // …an OLDER game-server sends no `lastTrick`: the play log has the trick mid-hand, and `leader`
  // is its winner (at a hand's end it has nothing, as before).
  const log = view.playLog ?? [];
  const last = log[log.length - 1];
  if (last === undefined || last.length !== view.seats) return null;
  return { sig: sigOf(last), plays: last, winner: view.leader, onFelt: false };
}

export function useTrickHold(view: TrickHoldView, rules: TrickRules): TrickHold {
  const newest = newestFinished(view, rules);
  // MOUNT: a finished trick the frame still shows is held and collected, as it always was; anything
  // older (a lead already down, the backstop's named trick) is adopted unseen — a resume or rejoin
  // replays nothing.
  const [latch, setLatch] = useState<Latch>(() =>
    newest === null
      ? NOTHING
      : { sig: newest.sig, plays: newest.plays, winner: newest.winner, held: newest.onFelt },
  );
  // LATCH DURING RENDER, never in an effect: the render where a newer finished trick first appears
  // must already draw it — an effect hands it over one render late, after the next lead has painted.
  // A newer trick replaces a held one at once (newest wins — your own card is in every trick and
  // your hand is disabled while one is held, so only the server playing for you can get here).
  let current = latch;
  if (newest !== null && newest.sig !== latch.sig) {
    current = { sig: newest.sig, plays: newest.plays, winner: newest.winner, held: true };
    setLatch(current);
  }
  const { sig, held, winner } = current;

  // RELEASE after the whole hold + collect from the trick's first render. Keyed on the signature
  // (a string) and the flag, so new frame objects with the same trick neither restart nor end it.
  useEffect(() => {
    if (!held) return undefined;
    const timer = setTimeout(
      () => setLatch((l) => (l.sig === sig && l.held ? { ...l, held: false } : l)),
      TRICK_HOLD_MS + TRICK_COLLECT_MS,
    );
    return () => clearTimeout(timer);
  }, [sig, held]);

  // THE COLLECT — ONE WAAPI animation that holds for TRICK_HOLD_MS (its delay) then sweeps toward
  // the winner over TRICK_COLLECT_MS; started once per trick, and dropped ONLY on release, in a
  // LAYOUT effect, so the live cards (same layer) never inherit its translate/fade on first paint.
  // A runtime without `.animate` (happy-dom / SSR) skips the flourish; the release still happens.
  const layerRef = useRef<HTMLDivElement>(null);
  const sweep = useRef<{ sig: string; anim: Animation | null }>({ sig: "", anim: null });
  const winnerRel = (winner - view.you + view.seats) % view.seats;
  useLayoutEffect(() => {
    const s = sweep.current;
    if (!held) {
      s.anim?.cancel();
      s.anim = null;
      return;
    }
    if (s.sig === sig) return; // once per trick: reflow, timer ticks and StrictMode are no-ops
    const layer = layerRef.current;
    if (!layer || typeof layer.animate !== "function") return;
    s.anim?.cancel(); // a newer trick replaced the held one
    s.sig = sig;
    s.anim = layer.animate(
      [
        { transform: "translate(0px,0px) scale(1)", opacity: 1 },
        { transform: `${collectSweep(winnerRel)} scale(.62)`, opacity: 0 },
      ],
      {
        duration: TRICK_COLLECT_MS,
        delay: TRICK_HOLD_MS,
        easing: "cubic-bezier(.4,0,.2,1)",
        fill: "forwards",
      },
    );
  }, [held, sig, winnerRel]);

  const shown: TrickViewLike = held
    ? {
        seats: view.seats,
        you: view.you,
        turn: winner,
        currentTrick: [],
        completedTrick: current.plays,
      }
    : {
        seats: view.seats,
        you: view.you,
        turn: view.turn,
        currentTrick: view.currentTrick,
        completedTrick: [],
      };
  return { holding: held, shown, layerRef };
}
